import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ArrowLeft, CheckCircle2, AlertTriangle, HelpCircle, RotateCcw, ShieldCheck, ShieldAlert, ShieldOff, Search } from "lucide-react";

const AUTOPAY = [
  ["congestion", "Congestion"],
  ["dartford", "Dartford Crossing"],
  ["tunnels", "Blackwall / Silvertown"],
  ["airport_heathrow", "Heathrow"],
  ["airport_gatwick", "Gatwick"],
  ["airport_stansted", "Stansted"],
  ["airport_luton", "Luton"],
];
const RATE_ZONES = [...AUTOPAY, ["ulez", "ULEZ"], ["lez", "LEZ"]];
const gbp = (n) => `£${(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ZoneBadge = ({ status }) => {
  if (status === "compliant")
    return <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-[#32D74B] text-xs font-bold"><CheckCircle2 size={13} /> Compliant</span>;
  if (status === "chargeable")
    return <span className="inline-flex items-center gap-1 text-[#FF3B30] text-xs font-bold"><AlertTriangle size={13} /> Needs to pay</span>;
  return <span className="inline-flex items-center gap-1 text-muted-foreground/70 text-xs"><HelpCircle size={13} /> Unknown</span>;
};

const InsuranceBadge = ({ ins }) => {
  if (ins?.status === "insured")
    return (
      <div>
        <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-[#32D74B] text-xs font-bold"><ShieldCheck size={13} /> Insured</span>
        <div className="text-[10px] text-muted-foreground truncate max-w-[130px]">{ins.insurer}{ins.expiry_date ? ` · to ${fmtDate(ins.expiry_date)}` : ""}</div>
      </div>
    );
  if (ins?.status === "expired")
    return (
      <div>
        <span className="inline-flex items-center gap-1 text-[#FF3B30] text-xs font-bold"><ShieldAlert size={13} /> Expired</span>
        <div className="text-[10px] text-muted-foreground truncate max-w-[130px]">{ins.insurer}{ins.expiry_date ? ` · ${fmtDate(ins.expiry_date)}` : ""}</div>
      </div>
    );
  return <span className="inline-flex items-center gap-1 text-[#FF3B30] text-xs font-bold"><ShieldOff size={13} /> Not insured</span>;
};

const UsageInput = ({ value, onSave, disabled, testid }) => {
  const [val, setVal] = useState(value ?? 0);
  useEffect(() => setVal(value ?? 0), [value]);
  const commit = () => {
    const n = Math.max(0, parseFloat(val) || 0);
    if (n !== (value ?? 0)) onSave(n);
    else setVal(value ?? 0);
  };
  return (
    <Input
      type="number"
      min="0"
      data-testid={testid}
      disabled={disabled}
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
      className="rounded-none bg-background border-border h-7 w-14 text-xs text-center px-1"
    />
  );
};

export default function ChargesPage() {
  const { canEdit } = useAuth();
  const editable = canEdit("vehicles");
  const [rows, setRows] = useState([]);
  const [rates, setRates] = useState(null);
  const [rateForm, setRateForm] = useState({});
  const [savingRates, setSavingRates] = useState(false);
  const [filter, setFilter] = useState("");

  const load = () => {
    api.get("/vehicles/charges").then((r) => setRows(r.data));
    api.get("/vehicles/zone-rates").then((r) => { setRates(r.data); setRateForm(r.data); });
  };
  useEffect(() => { load(); }, []);

  const saveRates = async () => {
    setSavingRates(true);
    try {
      const { data } = await api.put("/vehicles/zone-rates", { rates: rateForm });
      setRates(data);
      setRateForm(data);
      toast.success("Zone rates saved");
      api.get("/vehicles/charges").then((r) => setRows(r.data));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSavingRates(false);
    }
  };

  const putVehicle = async (v, payload) => {
    try {
      const { data } = await api.put(`/vehicles/${v.id}/charges`, payload);
      setRows((rs) => rs.map((r) => (r.id === v.id ? data : r)));
    } catch (e) {
      toast.error(errMsg(e));
      load();
    }
  };

  const cycleZone = (v, zone) => {
    const current = v[zone];
    const next = current === "compliant" ? "chargeable" : current === "chargeable" ? "" : "compliant";
    putVehicle(v, { [`${zone}_override`]: next });
  };

  const fleetTotal = rows.reduce((a, r) => a + (r.monthly_cost || 0), 0);
  const shown = rows.filter((v) => `${v.registration} ${v.make || ""} ${v.model || ""}`.toLowerCase().includes(filter.toLowerCase()));

  return (
    <div className="p-8" data-testid="charges-page">
      <Link to="/vehicles" data-testid="back-to-vehicles-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft size={14} /> Vehicles
      </Link>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter">CHARGE ZONES</h1>
          <p className="text-muted-foreground text-sm mt-1 max-w-3xl">
            Toggle <span className="text-foreground font-semibold">auto‑pay</span> per vehicle and enter how many charge days (or drop‑offs) each vehicle does per month — the estimated cost updates instantly. ULEZ &amp; LEZ only count when a vehicle needs to pay.
          </p>
        </div>
        <div className="flex items-end gap-6">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="charges-search-input"
              placeholder="Search reg, make…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="rounded-none bg-card border-border pl-9 w-56 h-10 font-mono uppercase"
            />
          </div>
          <div className="text-right" data-testid="fleet-monthly-total">
            <div className="uppercase text-[10px] tracking-[0.2em] font-bold text-muted-foreground">Est. fleet monthly</div>
            <div className="font-heading font-black text-3xl tracking-tighter">{gbp(fleetTotal)}</div>
          </div>
        </div>
      </div>

      {rates && (
        <div className="border border-border bg-card p-4 mb-6" data-testid="zone-rates-panel">
          <div className="flex items-end justify-between flex-wrap gap-3">
            <div className="flex items-end flex-wrap gap-4">
              <span className="uppercase text-[10px] tracking-[0.2em] font-bold text-muted-foreground pb-2">Daily rates (£)</span>
              {RATE_ZONES.map(([k, label]) => (
                <div key={k}>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">{label}</div>
                  <Input
                    type="number"
                    min="0"
                    step="0.5"
                    data-testid={`rate-${k}-input`}
                    disabled={!editable}
                    value={rateForm[k] ?? ""}
                    onChange={(e) => setRateForm((f) => ({ ...f, [k]: e.target.value }))}
                    className="rounded-none bg-background border-border h-8 w-20 text-xs"
                  />
                </div>
              ))}
            </div>
            {editable && (
              <Button data-testid="save-rates-button" onClick={saveRates} disabled={savingRates} className="rounded-none bg-[#FF3B30] hover:bg-[#ff554b] font-bold uppercase tracking-widest text-xs h-9">
                {savingRates ? "Saving…" : "Save rates"}
              </Button>
            )}
          </div>
        </div>
      )}

      <div className="border border-border bg-card overflow-x-auto overflow-y-auto max-h-[calc(100vh-230px)]">
        <table className="w-full text-sm" data-testid="charges-table">
          <thead className="sticky top-0 z-20 bg-card shadow-[0_1px_0_0_hsl(var(--border))]">
            <tr className="text-left">
              <th className="px-4 py-3 uppercase text-[10px] tracking-[0.2em] font-bold text-muted-foreground whitespace-nowrap sticky left-0 z-30 bg-card">Vehicle</th>
              <th className="px-3 py-3 uppercase text-[10px] tracking-[0.2em] font-bold text-muted-foreground whitespace-nowrap">Insured</th>
              {AUTOPAY.map(([k, label]) => (
                <th key={k} className="px-3 py-3 text-center whitespace-nowrap">
                  <div className="uppercase text-[10px] tracking-[0.2em] font-bold text-muted-foreground">{label}</div>
                  <div className="text-[9px] text-muted-foreground/60 normal-case tracking-normal">{gbp(rates?.[k])}/day · auto‑pay + days/mo</div>
                </th>
              ))}
              {["ulez", "lez"].map((z) => (
                <th key={z} className="px-3 py-3 whitespace-nowrap">
                  <div className="uppercase text-[10px] tracking-[0.2em] font-bold text-muted-foreground">{z.toUpperCase()}</div>
                  <div className="text-[9px] text-muted-foreground/60 normal-case tracking-normal">{gbp(rates?.[z])}/day if chargeable</div>
                </th>
              ))}
              <th className="px-4 py-3 uppercase text-[10px] tracking-[0.2em] font-bold text-muted-foreground text-right whitespace-nowrap">Est. / month</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((v) => (
              <tr key={v.id} data-testid={`charges-row-${v.registration}`} className="border-b border-border/60 hover:bg-muted/60">
                <td className="px-4 py-3 whitespace-nowrap sticky left-0 bg-card">
                  <div className="font-mono font-bold">{v.registration}</div>
                  <div className="text-xs text-muted-foreground">{[v.make, v.model].filter(Boolean).join(" ") || "—"}</div>
                </td>
                <td className="px-3 py-3 whitespace-nowrap" data-testid={`insured-${v.registration}`}>
                  <InsuranceBadge ins={v.insurance} />
                </td>
                {AUTOPAY.map(([k]) => (
                  <td key={k} className="px-3 py-3 text-center">
                    <div className="flex flex-col items-center gap-1.5">
                      <Switch
                        data-testid={`charge-${k}-${v.registration}`}
                        checked={!!v.charges[k]}
                        disabled={!editable}
                        onCheckedChange={() => putVehicle(v, { charges: { [k]: !v.charges[k] } })}
                      />
                      <UsageInput
                        testid={`usage-${k}-${v.registration}`}
                        value={v.zone_usage?.[k] ?? 0}
                        disabled={!editable}
                        onSave={(n) => putVehicle(v, { zone_usage: { [k]: n } })}
                      />
                    </div>
                  </td>
                ))}
                {["ulez", "lez"].map((zone) => (
                  <td key={zone} className="px-3 py-3 whitespace-nowrap">
                    <div className="flex flex-col gap-1.5">
                      <div className="flex items-center gap-1.5">
                        <button
                          data-testid={`zone-${zone}-${v.registration}`}
                          onClick={() => editable && cycleZone(v, zone)}
                          disabled={!editable}
                          className={editable ? "cursor-pointer" : "cursor-default"}
                          title={editable ? "Click to override (Compliant → Needs to pay → Auto)" : ""}
                        >
                          <ZoneBadge status={v[zone]} />
                        </button>
                        {v[`${zone}_override`] && (
                          <button
                            data-testid={`zone-${zone}-reset-${v.registration}`}
                            onClick={() => editable && putVehicle(v, { [`${zone}_override`]: "" })}
                            className="text-muted-foreground/60 hover:text-foreground"
                            title="Reset to auto-detected"
                          >
                            <RotateCcw size={12} />
                          </button>
                        )}
                      </div>
                      {v[zone] === "chargeable" && (
                        <UsageInput
                          testid={`usage-${zone}-${v.registration}`}
                          value={v.zone_usage?.[zone] ?? 0}
                          disabled={!editable}
                          onSave={(n) => putVehicle(v, { zone_usage: { [zone]: n } })}
                        />
                      )}
                    </div>
                  </td>
                ))}
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <span data-testid={`monthly-cost-${v.registration}`} className={`font-mono font-bold ${v.monthly_cost > 0 ? "" : "text-muted-foreground/50"}`}>{gbp(v.monthly_cost)}</span>
                </td>
              </tr>
            ))}
            {shown.length === 0 && (
              <tr><td colSpan={12} className="px-4 py-12 text-center text-muted-foreground/70">{rows.length === 0 ? "No vehicles yet. Add vehicles to manage their charge zones." : "No vehicle matches your search."}</td></tr>
            )}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr className="border-t border-border">
                <td className="px-4 py-3 uppercase text-[10px] tracking-[0.2em] font-bold text-muted-foreground sticky left-0 bg-card">Fleet total</td>
                <td colSpan={10}></td>
                <td className="px-4 py-3 text-right font-mono font-black" data-testid="fleet-total-cell">{gbp(fleetTotal)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
